import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Grid
} from '@mui/material';
import { 
  CheckCircle, 
  Email, 
  FlightTakeoff, 
  CalendarMonth,
  Person,
  ArrowForward,
  Flight,
  Info,
  ContentCopy
} from '@mui/icons-material';
import api from '../config/api';

function FlightBookingConfirmation() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Utility functions to safely extract string values from objects
  const getAirportCode = (location) => {
    if (!location) return 'N/A';
    if (typeof location === 'string') return location;
    return location.iata_code || location.airport || location.iata_city_code || 'N/A';
  };

  const getCityName = (location) => {
    if (!location) return 'N/A';
    if (typeof location === 'string') return location;
    return location.city_name || location.city?.name || location.city?.city_name || 
           location.name || location.iata_code || 'N/A';
  };

  const getAirlineName = (carrier) => {
    if (!carrier) return 'N/A';
    if (typeof carrier === 'string') return carrier;
    return carrier.name || carrier.iata_code || 'N/A';
  };

  const getAircraftName = (aircraft) => {
    if (!aircraft) return 'N/A';
    if (typeof aircraft === 'string') return aircraft;
    return aircraft.name || aircraft.iata_code || 'N/A';
  };
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError(t('booking.error.notFound'));
      setLoading(false);
      return;
    }

    const fetchBooking = async () => {
      try {
        setLoading(true);
        
        console.log('📋 Fetching booking for session:', sessionId);
        const response = await api.get(`/payments/booking-details/${sessionId}`);
        
        if (response.data.success && response.data.booking) {
          console.log('✅ Booking loaded successfully');
          setBooking(response.data.booking);
        } else {
          throw new Error('Booking not found');
        }
      } catch (e) {
        console.error('❌ Error fetching booking:', e);
        const errorData = e.response?.data || {};
        const errorMessage = errorData.message || e.message || 'Unknown error';
        const wasCharged = errorData.wasCharged;
        const urgent = errorData.urgent;
        
        // Check if this is a failed booking scenario
        if (wasCharged === false) {
          // Payment was NOT captured - this is the good scenario
          setError(
            `❌ ${t('booking.error.failedTitle')}\n\n` +
            `${t('booking.error.failedMessage')}\n\n` +
            `✅ ${t('booking.error.notChargedTitle')}\n\n` +
            `${t('booking.error.notChargedMessage')}\n\n` +
            `${t('booking.error.sessionIdLabel')} ${sessionId}`
          );
        } else if (urgent && wasCharged) {
          // Payment WAS captured but no booking exists - URGENT
          setError(
            `🚨 ${t('booking.error.urgentTitle')}\n\n` +
            `${errorMessage}\n\n` +
            `${t('booking.error.sessionIdShort')} ${sessionId}\n\n` +
            `${t('booking.error.urgentMessage')}`
          );
        } else {
          // Other errors - booking might still be processing
          setError(`${t('booking.error.title')}: ${errorMessage}. ${t('booking.error.processingMessage')} ${sessionId}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [sessionId, t]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Confirming your booking...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a few seconds
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              ❌ Booking Error
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {error}
            </Typography>
            {sessionId && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary">
                  Session ID (for support):
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {sessionId}
                </Typography>
              </Box>
            )}
          </Alert>
          
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} justifyContent="center">
            <Button 
              variant="contained" 
              onClick={() => navigate('/trips')}
            >
              View My Trips
            </Button>
            <Button 
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Retry Loading
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Get flight segments
  const flightDetails = booking?.flight_details;
  const slices = flightDetails?.slices || [];
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Render a single flight slice (outbound or return)
  const renderFlightSlice = (slice, index) => {
    if (!slice || !slice.segments || slice.segments.length === 0) return null;
    
    const firstSegment = slice.segments[0];
    const lastSegment = slice.segments[slice.segments.length - 1];
    const isReturn = index === 1;

    return (
      <Paper 
        key={index}
        elevation={0} 
        sx={{ 
          mb: 3, 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        {/* Flight Header */}
        <Box 
          sx={{ 
            px: 3, 
            py: 2, 
            bgcolor: '#f8f9fa',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Flight sx={{ transform: isReturn ? 'rotate(90deg)' : 'rotate(45deg)', color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              {isReturn ? 'Return Flight' : 'Outbound Flight'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {formatDate(firstSegment.origin?.time)}
            </Typography>
          </Box>
        </Box>

        {/* Flight Route */}
        <Box sx={{ p: 3 }}>
          <Grid container alignItems="center" spacing={2}>
            {/* Departure */}
            <Grid item xs={12} sm={5}>
              <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1 }}>
                {formatTime(firstSegment.departing_at || firstSegment.origin?.time)}
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                {getAirportCode(firstSegment.origin)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getCityName(firstSegment.origin)}
              </Typography>
              {(firstSegment.origin_terminal || firstSegment.origin?.terminal) && (
                <Typography variant="caption" color="text.secondary">
                  Terminal {firstSegment.origin_terminal || firstSegment.origin.terminal}
                </Typography>
              )}
            </Grid>

            {/* Duration & Stops */}
            <Grid item xs={12} sm={2}>
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {slice.duration || 'N/A'}
                </Typography>
                <Box 
                  sx={{ 
                    position: 'relative',
                    height: 2, 
                    bgcolor: 'primary.main',
                    my: 1,
                    '&::before': slice.segments.length > 1 ? {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'primary.main'
                    } : {}
                  }} 
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {slice.segments.length === 1 ? 'Direct' : `${slice.segments.length - 1} stop${slice.segments.length > 2 ? 's' : ''}`}
                </Typography>
              </Box>
            </Grid>

            {/* Arrival */}
            <Grid item xs={12} sm={5}>
              <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1 }}>
                {formatTime(lastSegment.arriving_at || lastSegment.destination?.time)}
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                {getAirportCode(lastSegment.destination)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getCityName(lastSegment.destination)}
              </Typography>
              {(lastSegment.destination_terminal || lastSegment.destination?.terminal) && (
                <Typography variant="caption" color="text.secondary">
                  Terminal {lastSegment.destination_terminal || lastSegment.destination.terminal}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Segments Details */}
          {slice.segments.map((segment, segIdx) => (
            <Box key={segIdx} sx={{ mt: 3, pt: 3, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Airline
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {getAirlineName(segment.marketing_carrier || segment.operating_carrier)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Flight
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {segment.marketing_carrier_flight_number || 
                     `${segment.marketing_carrier?.iata_code || ''} ${segment.marketing_flight_number || ''}`.trim() || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Aircraft
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {getAircraftName(segment.aircraft)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Class
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    Economy
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };
  
  return (
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
      {/* Success Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 3, md: 5 }, 
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <CheckCircle sx={{ fontSize: { xs: 60, md: 80 }, mb: 2 }} />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '3rem' } }}>
          Booking Confirmed!
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.95, mb: 3 }}>
          Your flight has been successfully booked
        </Typography>
        {booking?.booking_reference && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="overline" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
              Booking Reference
            </Typography>
            <Box 
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                p: 2, 
                bgcolor: 'rgba(255,255,255,0.15)', 
                borderRadius: 2,
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 3 }}>
                {booking.booking_reference}
              </Typography>
              <Button
                size="small"
                onClick={() => copyToClipboard(booking.booking_reference)}
                sx={{ 
                  color: 'white',
                  minWidth: 'auto',
                  p: 1
                }}
              >
                <ContentCopy fontSize="small" />
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Flight Details */}
        <Grid item xs={12} md={8}>
          
          {/* Render all flight slices */}
          {slices.map((slice, index) => renderFlightSlice(slice, index))}

          {/* Passenger Details */}
          {booking?.passengers && booking.passengers.length > 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  px: 3, 
                  py: 2, 
                  bgcolor: '#f8f9fa',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Passengers
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ p: 3 }}>
                {booking.passengers.map((passenger, idx) => (
                  <Box key={idx}>
                    {idx > 0 && <Divider sx={{ my: 2 }} />}
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Passenger {idx + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Full Name
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {passenger.title} {passenger.firstName} {passenger.lastName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Date of Birth
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {passenger.dateOfBirth}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Email
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {passenger.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Phone
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {passenger.phone}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Baggage Information */}
          <Paper 
            elevation={0}
            sx={{ 
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                px: 3, 
                py: 2, 
                bgcolor: '#f8f9fa',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlightTakeoff color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Baggage Allowance
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Alert severity="info" icon={<Info />} sx={{ bgcolor: '#e3f2fd' }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Carry-on: <Typography component="span" variant="body2" fontWeight={400}>1 bag included (subject to airline policy)</Typography>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Checked bags: <Typography component="span" variant="body2" fontWeight={400}>Check with the airline for baggage fees and policies</Typography>
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pt: 1 }}>
                    Baggage allowances are confirmed in your e-ticket. Standard airline rules apply.
                  </Typography>
                </Stack>
              </Alert>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Summary & Next Steps */}
        <Grid item xs={12} md={4}>
          
          {/* Pricing Summary */}
          {booking?.total_amount && (
            <Paper 
              elevation={0}
              sx={{ 
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  px: 3, 
                  py: 2, 
                  bgcolor: '#f8f9fa',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Payment Summary
                </Typography>
              </Box>
              
              <Box sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Flight Cost</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(booking.total_amount, booking.currency)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Service Fee</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(booking.customer_paid_amount - booking.total_amount, booking.currency)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Total Paid</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {formatCurrency(booking.customer_paid_amount, booking.currency)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          )}

          {/* What's Next */}
          <Paper 
            elevation={0}
            sx={{ 
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                px: 3, 
                py: 2, 
                bgcolor: '#f8f9fa',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                What's Next?
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Email sx={{ color: 'primary.main', flexShrink: 0, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Check Your Email
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Your e-ticket and booking details have been sent
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <CalendarMonth sx={{ color: 'primary.main', flexShrink: 0, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Add to Calendar
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Don't forget your flight times
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FlightTakeoff sx={{ color: 'primary.main', flexShrink: 0, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Arrive Early
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2-3 hours before departure
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Person sx={{ color: 'primary.main', flexShrink: 0, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Valid ID Required
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Bring passport for international flights
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Stack spacing={2}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate('/my-bookings')}
              endIcon={<ArrowForward />}
              sx={{
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #66388f 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                }
              }}
            >
              View My Trips
            </Button>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => navigate('/deals')}
              sx={{ 
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2
                }
              }}
            >
              Book Another Flight
            </Button>
          </Stack>

          {/* Support */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Need help? <Typography 
                component="a" 
                href="mailto:support@dejavunomad.com" 
                sx={{ 
                  color: 'primary.main', 
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Contact Support
              </Typography>
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Booking Reference Footer */}
      {booking?.duffel_order_id && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mt: 3, 
            bgcolor: '#f8f9fa',
            border: '1px dashed #dee2e6'
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Duffel Order ID: {booking.duffel_order_id}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Session ID: {sessionId}
          </Typography>
        </Paper>
      )}
    </Container>
  );
}

export default FlightBookingConfirmation;
